/**
 * SettingsNav — grouped vertical settings navigation.
 *
 * Two homes:
 *   - `variant="sidebar"` (default): SettingsLayout's sticky left sidebar
 *     at `md` and up. Groups are always visible (no collapse) because the
 *     sidebar has its own scroll. Width is fixed by the parent so
 *     navigating between pages never reflows.
 *   - `variant="sheet"`: the mobile bottom sheet opened from the section
 *     switcher below `md`. Same groups, larger 44px touch rows.
 *
 * Active items get a filled background pill + left accent bar.
 */
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import {
  BadgePercent,
  Banknote,
  BarChart3,
  BellRing,
  Bot,
  Building2,
  CalendarDays,
  CalendarX,
  Car,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Columns3,
  CreditCard,
  FileSignature,
  Flag,
  Gift,
  Globe,
  Grid3X3,
  Hash,
  Home,
  KeyRound,
  Link as LinkIcon,
  ListChecks,
  Mail,
  Moon,
  Package,
  Palette,
  Phone,
  Printer,
  Repeat,
  Scissors,
  Settings2,
  ShieldCheck,
  ShieldPlus,
  Sparkles,
  Star,
  Stethoscope,
  Syringe,
  Tablet,
  Ticket,
  Truck,
  Upload,
  Users,
  Warehouse,
  Zap,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';

export interface SettingsNavEntry {
  to: string;
  label: string;
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}

export interface SettingsNavGroup {
  label: string;
  items: SettingsNavEntry[];
}

export const SETTINGS_GROUPS: SettingsNavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { to: '/settings/account', label: 'Account', Icon: Settings2 },
      { to: '/settings/facilities', label: 'Facilities', Icon: Warehouse },
      { to: '/settings/buildings', label: 'Buildings', Icon: Building2 },
      { to: '/settings/runs', label: 'Runs', Icon: Home },
      { to: '/settings/services', label: 'Services', Icon: Sparkles },
      { to: '/settings/assessments', label: 'Evaluation days', Icon: ClipboardCheck },
      { to: '/settings/team', label: 'Team', Icon: Users },
      { to: '/settings/team/roles', label: 'Roles', Icon: ShieldCheck },
      { to: '/settings/waiver', label: 'Waiver', Icon: FileSignature },
      { to: '/settings/compliance', label: 'Compliance', Icon: ClipboardCheck },
      { to: '/settings/kiosks', label: 'Kiosks', Icon: Tablet },
      { to: '/settings/vets', label: 'Vets', Icon: Stethoscope },
      { to: '/settings/import', label: 'Import data', Icon: Upload },
    ],
  },
  {
    label: 'Branding',
    items: [
      { to: '/settings/branding', label: 'Branding', Icon: Palette },
      { to: '/settings/custom-domain', label: 'Custom domain', Icon: Globe },
      { to: '/settings/run-card', label: 'Run cards', Icon: Printer },
      { to: '/settings/report-cards', label: 'Report cards', Icon: ClipboardList },
    ],
  },
  {
    label: 'Billing',
    items: [
      { to: '/settings/billing', label: 'Subscription', Icon: CreditCard },
      { to: '/settings/payments', label: 'Payments', Icon: Banknote },
      { to: '/settings/memberships', label: 'Memberships', Icon: Repeat },
      { to: '/settings/discounts', label: 'Discounts', Icon: Ticket },
      { to: '/settings/pricing-rules', label: 'Pricing rules', Icon: BadgePercent },
      { to: '/settings/cancellation-policy', label: 'Cancellation', Icon: CalendarX },
      { to: '/settings/loyalty', label: 'Loyalty', Icon: Star },
      { to: '/settings/gift-cards', label: 'Gift cards', Icon: Gift },
      { to: '/settings/packages', label: 'Packages', Icon: Package },
      { to: '/settings/insurance', label: 'Insurance', Icon: ShieldPlus },
    ],
  },
  {
    label: 'Communications',
    items: [
      { to: '/settings/email-sending-domain', label: 'Email domain', Icon: Mail },
      { to: '/settings/sms-provider', label: 'SMS / WhatsApp', Icon: Phone },
      { to: '/settings/scheduled-reports', label: 'Scheduled reports', Icon: Clock },
    ],
  },
  {
    label: 'POS',
    items: [
      { to: '/settings/pos/products', label: 'Products', Icon: Package },
      { to: '/settings/pos/stock', label: 'Stock', Icon: BarChart3 },
      { to: '/settings/pos/sessions', label: 'Till sessions', Icon: ClipboardList },
      { to: '/settings/pos/printer', label: 'Printer', Icon: Printer },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/settings/grooming/groomers', label: 'Groomers', Icon: Scissors },
      { to: '/settings/daycare/capacity', label: 'Daycare capacity', Icon: Grid3X3 },
      { to: '/settings/daycare/late-pickup', label: 'Daycare late pickup', Icon: Clock },
      { to: '/settings/boarding/overstays', label: 'Boarding overstays', Icon: Moon },
      { to: '/settings/vaccines', label: 'Vaccines', Icon: Syringe },
      { to: '/settings/vaccines/policies', label: 'Vaccine policies', Icon: ShieldCheck },
      { to: '/settings/intake-questions', label: 'Intake questions', Icon: ClipboardList },
      { to: '/settings/custom-fields', label: 'Custom fields', Icon: ListChecks },
      { to: '/settings/booking-statuses', label: 'Booking statuses', Icon: Flag },
      { to: '/settings/task-board', label: 'Task board', Icon: Columns3 },
      { to: '/settings/tags', label: 'Tags', Icon: Hash },
      { to: '/settings/transport', label: 'Transport', Icon: Truck },
      { to: '/settings/vehicles', label: 'Vehicles', Icon: Car },
      { to: '/settings/staff-schedule', label: 'Staff schedule', Icon: CalendarDays },
    ],
  },
  {
    label: 'Marketing',
    items: [{ to: '/settings/marketing/postcards', label: 'Postcards', Icon: Mail }],
  },
  {
    label: 'AI',
    items: [{ to: '/settings/copilot', label: 'Copilot', Icon: Bot }],
  },
  {
    label: 'Integrations',
    items: [
      { to: '/settings/integrations', label: 'Integrations', Icon: LinkIcon },
      { to: '/settings/integrations/zapier', label: 'Webhooks', Icon: Zap },
      { to: '/settings/ops-alerts', label: 'Ops alerts', Icon: BellRing },
      { to: '/settings/api-keys', label: 'API keys', Icon: KeyRound },
    ],
  },
];

/**
 * Resolve the settings entry (and its group) for a pathname. Longest
 * matching prefix wins so `/settings/team/roles` resolves to Roles, not
 * Team, and `/settings/vaccines/policies` to Vaccine policies.
 */
export function findSettingsSection(
  pathname: string,
): { group: SettingsNavGroup; entry: SettingsNavEntry } | null {
  let best: { group: SettingsNavGroup; entry: SettingsNavEntry } | null = null;
  for (const group of SETTINGS_GROUPS) {
    for (const entry of group.items) {
      const matches = pathname === entry.to || pathname.startsWith(`${entry.to}/`);
      if (matches && (!best || entry.to.length > best.entry.to.length)) {
        best = { group, entry };
      }
    }
  }
  return best;
}

/**
 * Migration 0097 — the two space-related entries follow the tenant's noun
 * ("Pens" / "Pen cards" for a cattery); every other label is static.
 */
export function settingsLabelFor(
  to: string,
  label: string,
  noun: { Singular: string; Plural: string },
): string {
  if (to === '/settings/runs') return noun.Plural;
  if (to === '/settings/run-card') return `${noun.Singular} cards`;
  return label;
}

interface SettingsNavProps {
  variant?: 'sidebar' | 'sheet';
  /** Called after a link is chosen — the mobile sheet uses it to close. */
  onNavigate?: () => void;
}

export function SettingsNav({ variant = 'sidebar', onNavigate }: SettingsNavProps) {
  const noun = useSpaceNoun();
  const labelFor = (to: string, label: string): string => settingsLabelFor(to, label, noun);
  const sheet = variant === 'sheet';
  return (
    <nav aria-label="Settings sections" className={sheet ? 'space-y-4' : 'space-y-5'}>
      {SETTINGS_GROUPS.map((group) => (
        <div key={group.label}>
          <p className="mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {group.label}
          </p>
          <ul className="space-y-0.5">
            {group.items.map(({ to, label, Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={to === '/settings/team'}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    [
                      'relative flex items-center gap-2.5 rounded-[9px] text-[13.5px] font-medium tracking-tight transition-colors press',
                      sheet ? 'min-h-11 px-3 py-2.5' : 'px-2.5 py-1.5',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                    ].join(' ')
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          aria-hidden
                          className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-primary"
                        />
                      )}
                      <Icon
                        className={[
                          sheet ? 'size-4 shrink-0' : 'size-3.5 shrink-0',
                          isActive ? 'text-primary' : 'text-muted-foreground',
                        ].join(' ')}
                        strokeWidth={1.75}
                      />
                      {labelFor(to, label)}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}
